import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Typography, Box, TablePagination, CircularProgress, IconButton, Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import Layout from '../../components/Layout/Layout';
import PageHeader from '../../components/Common/PageHeader';
import { getPublisherContentApi, downloadPublisherContentApi } from '../../api/uploads';

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  window.URL.revokeObjectURL(url);
}

const STATUS_COLOR = { Pending: 'warning', Published: 'success' };

export default function Submissions() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPublisherContentApi({ page: page + 1, page_size: rowsPerPage });
      setRows(res.data.results || res.data);
      setTotal(res.data.count || res.data.length);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDownload = async (row) => {
    try {
      const res = await downloadPublisherContentApi(row.id);
      downloadBlob(res.data, row.original_file_name);
    } catch {
      // ignore
    }
  };

  return (
    <Layout title="My Submissions">
      <PageHeader
        title="My Submissions"
        subtitle="Track your uploaded content"
        breadcrumbs={[{ label: 'Publisher' }, { label: 'My Submissions' }]}
      />

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>File Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Content Type</TableCell>
                    <TableCell>Submitted On</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Published On</TableCell>
                    <TableCell>Books Created</TableCell>
                    <TableCell>Remarks</TableCell>
                    <TableCell>Download</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Tooltip title={r.original_file_name}><span>{r.original_file_name}</span></Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip label={r.file_type?.toUpperCase()} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {r.content_type ? <Chip label={r.content_type} size="small" color="info" variant="outlined" /> : '—'}
                      </TableCell>
                      <TableCell>{new Date(r.uploaded_date).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Chip label={r.status} size="small" color={STATUS_COLOR[r.status] || 'default'} />
                      </TableCell>
                      <TableCell>
                        {r.published_date ? new Date(r.published_date).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell>{r.books_created > 0 ? r.books_created : '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.remarks || '—'}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Download">
                          <IconButton size="small" onClick={() => handleDownload(r)}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No submissions yet. Upload your first file.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={total}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
}
